import { useEffect, useRef, MutableRefObject } from 'react';
import { OutgoingMessage, IncomingMessage, WebSocketHandlers } from '../types/websocket.types';

export const useWebSocket = (handlersRef: MutableRefObject<WebSocketHandlers>, username: string) => {
  const socketRef = useRef<WebSocket | null>(null);
  const currentUsernameRef = useRef<string>(username);

  const sendMessage = (message: OutgoingMessage) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  };

  useEffect(() => {
    // Only create a new connection if we don't have one or if it's closed
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // If connection is open but username changed, just send the username update
      if (currentUsernameRef.current !== username) {
        sendMessage({
          type: 'set_username',
          username: username
        });
        currentUsernameRef.current = username;
      }
      return;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/`;
    const webSocket = new WebSocket(wsUrl);
    socketRef.current = webSocket;
    currentUsernameRef.current = username;

    webSocket.onopen = () => {
      console.log('WebSocket connected');
      
      // Send username on connection
      sendMessage({
        type: 'set_username',
        username: username
      });
      
      // Query admin status
      sendMessage({
        type: 'query_admin_status'
      });
    };

    webSocket.onmessage = (event) => {
      try {
        const message: IncomingMessage = JSON.parse(event.data);
        
        switch (message.type) {
          case 'update_layer':
            if (message.layer && message.layer.properties && message.layer.properties.id) {
              handlersRef.current.onLayerUpdate?.(message.layer);
            }
            break;
          
          case 'delete_layer':
            if (message.layer_id) {
              handlersRef.current.onLayerDelete?.(message.layer_id);
            }
            break;
          
          case 'update_canopy_prediction':
            if (message.cutawayParameters && message.predictionGeojson) {
              handlersRef.current.onCanopyPrediction?.(message);
            }
            break;
          
          case 'user_list':
            if (message.users) {
              handlersRef.current.onUserList?.(message.users);
            }
            break;
          
          case 'username_response':
            if (message.userId && message.username && message.requestId) {
              handlersRef.current.onUsernameResponse?.(message.userId, message.username, message.requestId);
            }
            break;
          
          case 'admin_status_response':
            if (message.isAdmin !== undefined) {
              handlersRef.current.onAdminStatusResponse?.(message.isAdmin);
            }
            break;
          
          case 'tracks_response':
            if (message.tracks) {
              handlersRef.current.onTracksResponse?.(message.tracks);
            }
            break;
          
          case 'track_points_response':
            if (message.trackId && message.points) {
              handlersRef.current.onTrackPointsResponse?.(message.trackId, message.points);
            }
            break;
          
          case 'update_user_location':
            if (message.userId && message.trackId && message.latitude !== undefined && message.longitude !== undefined) {
              handlersRef.current.onUserLocationUpdate?.(message as any);
            }
            break;
          
          case 'error':
            // Handle standardized error format
            console.error(`${message.code || 'Error'}:`, message.message);
            if (message.code === 'RATE_LIMIT_ERROR' && (message as any).retryAfter) {
              console.warn(`Retry after ${(message as any).retryAfter}ms`);
            }
            // You could show a toast notification here
            break;
          
          case 'validation_error':
            // Keep backward compatibility for old format
            console.error('Validation error:', (message as any).error);
            break;
          
          case 'rate_limit_error':
            // Keep backward compatibility for old format
            console.warn('Rate limit error:', (message as any).error);
            if ((message as any).retryAfter) {
              console.warn(`Retry after ${(message as any).retryAfter}ms`);
            }
            break;
          
          default:
            console.warn('Unknown message type:', (message as any).type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    webSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    webSocket.onclose = () => {
      console.log('WebSocket disconnected');
    };

    return () => {
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        webSocket.close();
      }
    };
  }, [username]); // Re-connect if username changes

  const isConnected = () => {
    return socketRef.current?.readyState === WebSocket.OPEN;
  };

  return {
    socketRef,
    sendMessage,
    isConnected
  };
};